// shared-lib/group-customization/index.js
//
// Pure data + math primitives shared between the web-app group editor and
// the server-side vanity / fixture builders — and, from layout-relationships
// step 1, the constraint solver itself (`constraintSolver.js`), so links are
// enforced at every commit site and not only in the editor. Only the
// editor's snap *suggestions* stay in web-app/src/.
//
// Usage (CommonJS, e.g. functions/):
//   const { createLink, mergeLinks } = require('@shared-lib/group-customization');
//
// Usage (web-app ESM via @shared-lib alias):
//   import { createLink } from '@shared-lib/group-customization';

const {
  worldToLocal,
  localToWorld,
  rotateLocalToAnchor,
  rotateAnchorToLocal,
  rpOffsetFrameTheta,
  computeRpOffset,
  computeRpRotYDiff,
  computeCenterLineX,
} = require('./transforms');
const {
  LINK_TYPES,
  LINK_SOURCES,
  createLink,
  genLinkId,
} = require('./createLink');
const { buildAnchorLocal, nearestByDx } = require('./anchorPose');
const { validateLink } = require('./validateLink');
const { mergeLinks, linkRefs, linkKey } = require('./mergeLinks');
const {
  snapshotGroupCustomization,
  applyCustomizationToAnchor,
  pairCopyMirrorChildren,
} = require('./snapshot');
const { REF_KINDS, normalizeRef, refKey, isFixedRef } = require('./refs');
const {
  ALWAYS_WALL_MOUNTED,
  isWallMounted,
  mountedWallId,
  wallFaceDzInAnchorFrame,
} = require('./mountRules');
const {
  PREFERRED_ANCHOR_TYPES,
  matchesAnchorType,
  isLowerCabinetLike,
  pickAnchor,
} = require('./anchorTypes');
const { stampUnplaced, unplacedOf } = require('./unplaced');
const { resolveGroup, reconcileRpLinks } = require('./resolveGroup');
const { reconcileGroupLinks } = require('./reconcileGroupLinks');
const {
  linksFor,
  groupLinksIn,
  runLinksIn,
  addRoomLinks,
  removeRoomLink,
  pruneDanglingRoomLinks,
  pruneDanglingGroupLinks,
  linkEndpointKeys,
} = require('./roomLinks');
const { solveConstraints } = require('./constraintSolver');
const {
  TYPE_ALIASES,
  canonicalType,
  RAIL_EPS,
  isInertLink,
} = require('./linkSemantics');

module.exports = {
  stampUnplaced,
  unplacedOf,
  // Frame transforms
  worldToLocal,
  localToWorld,
  rotateLocalToAnchor,
  rotateAnchorToLocal,
  // RP-link math
  rpOffsetFrameTheta,
  computeRpOffset,
  computeRpRotYDiff,
  computeCenterLineX,
  // Link factory
  LINK_TYPES,
  LINK_SOURCES,
  createLink,
  genLinkId,
  // Anchor-local pose (build-time link emitters)
  buildAnchorLocal,
  nearestByDx,
  // Validation
  validateLink,
  // Link merge (build-time auto-link reconciliation)
  mergeLinks,
  linkRefs,
  linkKey,
  // Snapshot + replay
  snapshotGroupCustomization,
  applyCustomizationToAnchor,
  pairCopyMirrorChildren,
  // Endpoint refs (typed; a bare id is an object ref)
  REF_KINDS,
  normalizeRef,
  refKey,
  isFixedRef,
  // Shared semantics: canonical type names, rail tolerance, inert copy links
  TYPE_ALIASES,
  canonicalType,
  RAIL_EPS,
  isInertLink,
  // Wall mounting (shared by the editor's snaps and the on-wall emitters)
  ALWAYS_WALL_MOUNTED,
  isWallMounted,
  mountedWallId,
  wallFaceDzInAnchorFrame,
  // Anchor choice, shared by the editor and resolveGroup
  PREFERRED_ANCHOR_TYPES,
  matchesAnchorType,
  isLowerCabinetLike,
  pickAnchor,
  // The solver and its world-space wrapper — links enforced everywhere
  solveConstraints,
  resolveGroup,
  reconcileRpLinks,
  reconcileGroupLinks,
  // Room-level link registry + the one accessor over both stores
  linksFor,
  groupLinksIn,
  runLinksIn,
  addRoomLinks,
  removeRoomLink,
  pruneDanglingRoomLinks,
  pruneDanglingGroupLinks,
  linkEndpointKeys,
};
